import { useState, useEffect, useRef, FC } from 'react';
import { Attachment, FileWithStream, Stream } from '@/library/types/files';
import { RotateDirectionType } from '../store/types';
import { getFileType } from '@/library/utils/files';
import { getFileStream, getFileMetadata } from '@/api/files/api';
import { isAttachment, PREVIEWABLE_FILE_TYPES } from './helpers';

import Spinner from '@/library/components/general/Spinner';
import Preview from './preview';
import useWindowWidth from '@/library/utils/hooks/useWindowWidth';

type GalleryCenterContainerProps = {
  file: Attachment | undefined | null;
  isGalleryHovered: boolean;
  rotateDirection: RotateDirectionType;
  updateGalleryFile: (fileId: number | string, updatedFile: Attachment) => void;
};

const isFileUrlExpired = (urlExpiresAt?: string): boolean => {
  if (!urlExpiresAt) return false;

  return new Date(urlExpiresAt).getTime() < Date.now();
};

const GalleryCenterContainer: FC<GalleryCenterContainerProps> = ({
  file,
  isGalleryHovered,
  rotateDirection,
  updateGalleryFile
}) => {
  const [containerWidth, setContainerWidth] = useState<number | undefined>();
  const [containerHeight, setContainerHeight] = useState<number | undefined>();

  const containerRef = useRef<HTMLDivElement>(null);
  const updateGalleryFileRef = useRef(updateGalleryFile);
  const windowWidth = useWindowWidth();

  updateGalleryFileRef.current = updateGalleryFile;

  const [isLoading, setIsLoading] = useState(false);
  const [fileStream, setFileStream] = useState<Stream>(null);
  const [resolvedMetadata, setResolvedMetadata] = useState<Attachment | null>(null);
  const [attemptedFileId, setAttemptedFileId] = useState<Attachment['id'] | null>(null);

  const activeFile =
    file && resolvedMetadata?.id === file.id ? { ...file, ...resolvedMetadata } : file;
  const fileType = getFileType(activeFile?.mime_type, activeFile?.extension);
  const missingPreviewUrl = Boolean(
    activeFile && PREVIEWABLE_FILE_TYPES.has(fileType.type) && !activeFile.url_inline
  );
  const urlExpired =
    isFileUrlExpired(activeFile?.url_expires_at) ||
    isFileUrlExpired(activeFile?.url_inline_expires_at);
  const needsEmailStream = fileType.type === 'eml';
  const shouldFetch = Boolean(
    activeFile?.id &&
      attemptedFileId !== activeFile.id &&
      (missingPreviewUrl || urlExpired || needsEmailStream)
  );

  useEffect(() => {
    if (!activeFile?.id || !shouldFetch) return;

    let cancelled = false;
    const fileId = activeFile.id;

    setIsLoading(true);
    setFileStream(null);

    const loadFile = async () => {
      try {
        if (missingPreviewUrl || urlExpired) {
          const updatedMetadata = await getFileMetadata(fileId);

          if (!cancelled && updatedMetadata) {
            setResolvedMetadata(updatedMetadata);
            updateGalleryFileRef.current(fileId, updatedMetadata);
          }
        }

        if (needsEmailStream) {
          const stream = await getFileStream(fileId);

          if (!cancelled) setFileStream(stream);
        }
      } catch (error) {
        console.error(error);
        if (!cancelled) setFileStream(null);
      } finally {
        if (!cancelled) {
          setAttemptedFileId(fileId);
          setIsLoading(false);
        }
      }
    };

    void loadFile();

    return () => {
      cancelled = true;
    };
  }, [activeFile?.id, missingPreviewUrl, needsEmailStream, shouldFetch, urlExpired]);

  useEffect(() => {
    if (containerRef.current?.clientWidth && containerRef.current.clientHeight) {
      setContainerWidth(containerRef.current.clientWidth);
      setContainerHeight(containerRef.current.clientHeight);
    }
  }, [windowWidth, containerRef.current?.clientWidth]);

  const parsedFile = isAttachment(fileStream)
    ? ({ ...activeFile, ...fileStream } as FileWithStream)
    : fileType.type === 'eml'
      ? ({ ...activeFile, stream: fileStream } as FileWithStream)
      : activeFile;

  return (
    <div
      ref={containerRef}
      className={`gallery__center--container w-100p ${
        rotateDirection !== 'top' && rotateDirection !== 'bottom'
          ? 'd-flex align-items-center justify-content-center'
          : ''
      }`}
    >
      {isLoading || shouldFetch ? (
        <div className="position-absolute top-50  start-0 w-100p text-center ">
          <Spinner size={48} className="gallery__center--container-loader" />
        </div>
      ) : !activeFile || !parsedFile ? null : (
        <Preview
          key={activeFile.id}
          isGalleryHovered={isGalleryHovered}
          file={parsedFile}
          containerWidth={containerWidth || 0}
          containerHeight={containerHeight || 0}
          type={fileType.type}
          rotateDirection={rotateDirection}
        />
      )}
    </div>
  );
};

export default GalleryCenterContainer;
